import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Loader2, Search } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const SCHEME_LABELS = {
  thr: 'THR',
  hcm: 'HCM',
  growth_monitoring: 'Growth Monitoring',
  field_monitoring: 'Field Monitoring',
};

export default function MultiSchemeBeneficiaryModal({
  isOpen,
  onClose,
  title,
  description,
  schemes,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const pageSize = 50;

  const schemesParam = useMemo(() => (schemes || []).join(','), [schemes]);

  useEffect(() => {
    if (!isOpen) return;
    // reset on open
    setQ('');
    setItems([]);
    setTotal(0);
    setPage(0);
    setError(null);
  }, [isOpen, schemesParam]);

  useEffect(() => {
    if (!isOpen) return;
    if (!schemesParam) return;

    const controller = new AbortController();

    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API_URL}/api/beneficiary-report/multi-scheme/beneficiaries`, {
          params: {
            schemes: schemesParam,
            match: 'all',
            q: q || undefined,
            offset: page * pageSize,
            limit: pageSize,
          },
          signal: controller.signal,
        });
        setItems(res.data?.items || []);
        setTotal(res.data?.total || 0);
      } catch (e) {
        if (e?.name === 'CanceledError') return;
        console.error('Error fetching multi-scheme beneficiaries:', e);
        setError('Failed to load beneficiary list. Please try again.');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
    return () => controller.abort();
  }, [isOpen, schemesParam, q, page]);

  if (!isOpen) return null;

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const canPrev = page > 0;
  const canNext = page + 1 < totalPages;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            {(schemes || []).map((s) => (
              <Badge key={s} className="bg-blue-600">
                {SCHEME_LABELS[s] || s}
              </Badge>
            ))}
            <span className="text-xs text-gray-500">
              {total.toLocaleString()} beneficiaries
            </span>
          </div>

          <div className="relative w-full sm:max-w-sm">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              className="pl-9"
              placeholder="Search beneficiary name..."
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(0);
              }}
            />
          </div>
        </div>

        {error && (
          <div className="p-3 rounded border border-red-200 bg-red-50 text-sm text-red-800">
            {error}
          </div>
        )}

        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="text-center">Type</TableHead>
                <TableHead>District</TableHead>
                <TableHead>Sector</TableHead>
                <TableHead>AWC</TableHead>
                <TableHead className="text-center">Age (months)</TableHead>
                <TableHead className="text-center">Mobile</TableHead>
                <TableHead className="text-center">Aadhaar</TableHead>
                <TableHead className="text-center">Monitored</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9}>
                    <div className="flex items-center justify-center py-8 text-gray-600">
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      Loading beneficiaries...
                    </div>
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9}>
                    <div className="py-8 text-center text-gray-500">
                      No beneficiaries found for this combination.
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((b) => (
                  <TableRow key={b.id}>
                    <TableCell className="font-medium">{b.beneficiary_name || 'N/A'}</TableCell>
                    <TableCell className="text-center text-xs">
                      {b.beneficiary_type || 'N/A'}
                    </TableCell>
                    <TableCell>{b.district || 'N/A'}</TableCell>
                    <TableCell>{b.sector || 'N/A'}</TableCell>
                    <TableCell className="text-xs">
                      {b.awc_name || 'N/A'} {b.awc_code ? `(${b.awc_code})` : ''}
                    </TableCell>
                    <TableCell className="text-center">{b.age_months ?? '—'}</TableCell>
                    <TableCell className="text-center">{b.mobile_verified ? '✓' : '✗'}</TableCell>
                    <TableCell className="text-center">{b.aadhaar_verified ? '✓' : '✗'}</TableCell>
                    <TableCell className="text-center">{b.is_monitored ? '✓' : '✗'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-xs text-gray-500">
            Page {page + 1} of {totalPages}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={!canPrev || loading}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              onClick={() => setPage((p) => p + 1)}
              disabled={!canNext || loading}
            >
              Next
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


